/**
 * <Text> — canonical body / inline text primitive.
 *
 * Emits semantic markup (<p> by default, configurable via `as`) with one of
 * the design-system typography variants from docs/design-system/02-typography.md.
 *
 * The point of this primitive is to give every body / label / caption / metric
 * use site a single canonical token to choose from. Without it, the codebase
 * drifts to 20 distinct font-size signatures (per Phase-1 §2.5) for
 * conceptually identical text.
 *
 * Headings go through <Heading>, not <Text>.
 */

import { forwardRef, type HTMLAttributes, type ElementType, type ReactNode } from 'react';
import { cn } from '../utils/cn';

export type TextVariant =
  | 'body'           // 14px — default body text
  | 'body-md'        // 15px — emphasised body / dense forms
  | 'body-sm'        // 13px — table cells, captions
  | 'caption'        // 11px — chart annotations, timestamps, freshness
  | 'eyebrow'        // 11px UPPERCASE tracking — group labels above sections
  | 'kpi-value'      // 24px tabular-nums — dashboard KPI values
  | 'kpi-label'      // 11px UPPERCASE muted — dashboard KPI labels
  | 'mono'           // 13px JetBrains Mono — codes, IDs, timestamps
  | 'validation';    // 13px text-danger — inline validation copy

export type TextTone =
  | 'default'   // text-text
  | 'muted'     // text-muted
  | 'subtle'    // text-subtle
  | 'inverse'   // text-on-primary
  | 'success'
  | 'warning'
  | 'danger'
  | 'info';

export interface TextProps extends Omit<HTMLAttributes<HTMLElement>, 'children'> {
  variant?: TextVariant;
  tone?: TextTone;
  as?: ElementType;
  /** Render `font-display` (Fraunces serif). Marketing-only — D.3. */
  serif?: boolean;
  children: ReactNode;
}

const VARIANT_CLASSES: Record<TextVariant, string> = {
  body: 'text-base leading-normal',
  'body-md': 'text-md leading-normal',
  'body-sm': 'text-sm leading-snug',
  caption: 'text-xs leading-snug',
  eyebrow: 'text-xs font-semibold uppercase tracking-wider',
  'kpi-value': 'text-2xl font-semibold tabular-nums leading-none tracking-tight',
  'kpi-label': 'text-xs font-medium uppercase tracking-wider',
  mono: 'text-sm font-mono tabular-nums',
  validation: 'text-sm leading-snug',
};

const TONE_CLASSES: Record<TextTone, string> = {
  default: 'text-text',
  muted: 'text-muted',
  subtle: 'text-subtle',
  inverse: 'text-on-primary',
  success: 'text-success-muted',
  warning: 'text-warning-muted',
  danger: 'text-danger-muted',
  info: 'text-info-muted',
};

export const Text = forwardRef<HTMLElement, TextProps>(function Text(
  { variant = 'body', tone, as: Tag = 'p', serif, className, children, ...rest },
  ref,
) {
  // Some variants have a default tone; explicit `tone` always wins.
  const defaultTone: TextTone =
    variant === 'kpi-label' || variant === 'eyebrow'
      ? 'muted'
      : variant === 'validation'
        ? 'danger'
        : 'default';

  return (
    <Tag
      ref={ref as never}
      className={cn(
        VARIANT_CLASSES[variant],
        TONE_CLASSES[tone ?? defaultTone],
        serif && 'font-display',
        className,
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
});

Text.displayName = 'Text';

export default Text;
